/**
 * Página de conteo de un inventario (mobile-first): el preparador elige la nave
 * donde está, puntea cada artículo (suma 1 en esa nave) o escribe su cantidad
 * viendo el stock, guarda sin finalizar cuando quiera y finaliza al acabar
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";
import { useDetalleInventario, useStocks } from "../hooks";
import { TarjetaArticuloConteo, CargandoInventario } from "../components/ui";
import { idTarjetaConteo } from "../components/ui/TarjetaArticuloConteo";
import BuscadorConteo from "../components/forms/BuscadorConteo";
import SelectorNaveActiva from "../components/forms/SelectorNaveActiva";
import ModalQuitarNave from "../components/modals/ModalQuitarNave";
import { ESTADOS_FINALIZADOS } from "../constants";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX } from "../../../components/ui/buttonStyles";

// DECISION: no tiene ruta propia; DetalleInventario delega en ella en modo conteo y le pasa
// `rutaVolver` (/inventarios o /mis-inventarios) e `inventarioInicial` (el que ya cargó, para no
// repetir el GET). Sin ellos, como en sus tests, vuelve a /mis-inventarios y carga el inventario.
const ConteoInventario = ({ rutaVolver = "/mis-inventarios", inventarioInicial }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state, updateState } = useContext(StateContext);

    const {
        inventario,
        lineas,
        loading,
        error,
        pendientes,
        lineasPendientes,
        guardando,
        erroresLinea,
        almacenDe,
        almacenGeneral,
        seleccionarAlmacenGeneral,
        recuperados,
        descartarRecuperados,
        ocultarAvisoRecuperados,
        lineasSinContar,
        quitarNave,
        quitandoNave,
        sumarUno,
        fijarCantidad,
        seleccionarAlmacen,
        guardar,
        finalizar,
        puedeFinalizar,
    } = useDetalleInventario(id, inventarioInicial, { persistirBorrador: true });

    const [finalizando, setFinalizando] = useState(false);
    // Conteo guardado cuyo borrado se está confirmando: { lineaId, referencia, almacen, cantidadContada }
    const [naveAQuitar, setNaveAQuitar] = useState(null);

    const yaFinalizado = Boolean(inventario) && ESTADOS_FINALIZADOS.includes(inventario.estado);
    // Aquí y no en useDetalleInventario: DetalleInventario también usa ese hook y no necesita el stock
    const { stocks, ubicaciones } = useStocks(inventario?.lineas, Boolean(inventario) && !yaFinalizado);

    // Cerrar o recargar la pestaña con cantidades sin guardar las perdería
    useEffect(() => {
        if (pendientes === 0) return undefined;
        const avisar = (evento) => {
            evento.preventDefault();
            evento.returnValue = "";
        };
        window.addEventListener("beforeunload", avisar);
        return () => window.removeEventListener("beforeunload", avisar);
    }, [pendientes]);

    const toast = useCallback(
        (mensaje, tipo) => updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, tipo)] }),
        [state.toasts, updateState]
    );

    const handlePuntear = useCallback((linea) => sumarUno(linea.id), [sumarUno]);

    const handleGuardar = useCallback(async () => {
        const todoGuardado = await guardar();
        if (todoGuardado) toast("Conteo guardado", "success");
        else toast("Algunas cantidades no se guardaron. Revisa las líneas marcadas.", "error");
    }, [guardar, toast]);

    const handleFinalizar = useCallback(async () => {
        setFinalizando(true);
        try {
            await finalizar();
            toast("Inventario finalizado correctamente", "success");
            navigate(rutaVolver);
        } catch (err) {
            toast(err.message || "No se pudo finalizar el inventario.", "error");
        } finally {
            setFinalizando(false);
        }
    }, [finalizar, navigate, rutaVolver, toast]);

    // Lo que solo está en el borrador se descarta sin preguntar; lo guardado se borra en el servidor
    const handleQuitarNave = useCallback(
        (linea, conteo) => {
            if (!conteo.guardado) {
                quitarNave(linea.id, conteo.almacen);
                return;
            }
            setNaveAQuitar({ lineaId: linea.id, referencia: linea.referencia, ...conteo });
        },
        [quitarNave]
    );

    const confirmarQuitarNave = useCallback(async () => {
        const quitada = await quitarNave(naveAQuitar.lineaId, naveAQuitar.almacen);
        if (quitada) toast("Conteo de la nave quitado", "success");
        else toast("No se pudo quitar el conteo de esa nave.", "error");
        setNaveAQuitar(null);
    }, [naveAQuitar, quitarNave, toast]);

    const irAPrimeraSinContar = useCallback(() => {
        const primera = lineasSinContar[0];
        if (!primera) return;
        document.getElementById(idTarjetaConteo(primera.id))?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [lineasSinContar]);

    // Guardar no bloquea el punteo (el hook fusiona lo contado mientras guarda); finalizar
    // y quitar una nave sí: sus respuestas sustituyen el inventario entero
    const bloqueado = finalizando || quitandoNave !== null;

    // Las tarjetas (React.memo) reciben siempre las mismas funciones, que llaman a la última versión
    const acciones = useRef({});
    acciones.current = { fijarCantidad, sumarUno, seleccionarAlmacen, handleQuitarNave };
    const onFijar = useCallback((lineaId, cantidad) => acciones.current.fijarCantidad(lineaId, cantidad), []);
    const onSumar = useCallback((lineaId) => acciones.current.sumarUno(lineaId), []);
    const onSeleccionarAlmacen = useCallback((lineaId, almacen) => acciones.current.seleccionarAlmacen(lineaId, almacen), []);
    const onQuitarNave = useCallback((linea, conteo) => acciones.current.handleQuitarNave(linea, conteo), []);

    // EXCEPCION de CLAUDE.md (Estados de Carga, regla 4): pantalla de conteo,
    // el contenido se OCULTA mientras carga en vez de mostrarse tras un
    // overlay: puntear contra el stock teórico de un inventario equivocado es
    // el mismo riesgo operativo que ordenes/pages/Info.js.
    if (loading) return <CargandoInventario />;

    if (error || !inventario) {
        return (
            <Stack spacing={2} sx={{ width: "100%" }}>
                <Alert severity="error">{error || "No se pudo cargar el inventario solicitado."}</Alert>
                <Button variant="outlined" onClick={() => navigate(rutaVolver)} sx={{ ...BOTON_SECUNDARIO_SX, alignSelf: "flex-start" }}>
                    Volver
                </Button>
            </Stack>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 2, pb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <FactCheckIcon sx={{ fontSize: 24 }} />
                        Contar inventario
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#6c757d", mb: 2 }}>
                        {lineas.length} artículo(s) · Elige arriba la nave donde estás contando
                    </Typography>

                    {!yaFinalizado && recuperados > 0 && (
                        <Alert
                            severity="info"
                            // Con `action`, MUI no pinta el aspa de onClose: van las dos aquí
                            action={
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Button size="small" color="inherit" onClick={descartarRecuperados} sx={{ textTransform: "none", fontWeight: 600 }}>
                                        Descartar
                                    </Button>
                                    <IconButton size="small" color="inherit" onClick={ocultarAvisoRecuperados} aria-label="Cerrar aviso">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            }
                            sx={{ mb: 2 }}
                        >
                            Se han recuperado {recuperados} conteo(s) que no llegaste a guardar. Revísalos y pulsa Guardar.
                        </Alert>
                    )}

                    {yaFinalizado && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Este inventario ya está finalizado y no admite más conteos.
                        </Alert>
                    )}

                    {!yaFinalizado && (
                        <Stack spacing={2}>
                            <SelectorNaveActiva value={almacenGeneral} onChange={seleccionarAlmacenGeneral} disabled={bloqueado} />
                            <BuscadorConteo lineas={lineas} onPuntear={handlePuntear} disabled={bloqueado} />
                        </Stack>
                    )}
                </Box>

                <Box sx={{ px: 2, pb: 2 }}>
                    <Stack spacing={2}>
                        {lineas.map((linea) => (
                            <TarjetaArticuloConteo
                                key={linea.id}
                                linea={linea}
                                pendiente={lineasPendientes.has(linea.id)}
                                error={erroresLinea[linea.id]}
                                almacenSeleccionado={almacenDe(linea.id)}
                                almacenGeneral={almacenGeneral}
                                stockPorAlmacen={stocks[linea.referencia]}
                                ubicacion={ubicaciones[linea.referencia]}
                                onFijar={onFijar}
                                onSumar={onSumar}
                                onSeleccionarAlmacen={onSeleccionarAlmacen}
                                disabled={bloqueado}
                                quitarDeshabilitado={guardando}
                                soloLectura={yaFinalizado}
                                onQuitarNave={onQuitarNave}
                            />
                        ))}
                    </Stack>
                </Box>

                {!yaFinalizado && lineasSinContar.length > 0 && (
                    <Box sx={{ px: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, color: "#495057" }}>
                            Para finalizar faltan <strong>{lineasSinContar.length}</strong> artículo(s) por contar. Si alguno no
                            está en ninguna nave, cuenta 0 en la nave donde debería estar.
                        </Typography>
                        <Button size="small" onClick={irAPrimeraSinContar} sx={{ textTransform: "none", fontWeight: 600, color: "#cc071e", "&:hover": { backgroundColor: "rgba(204,7,30,0.04)" } }}>
                            Ir al primero
                        </Button>
                    </Box>
                )}

                {!yaFinalizado && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ p: 2, pt: 0 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            disabled={pendientes === 0 || guardando || bloqueado}
                            onClick={handleGuardar}
                            startIcon={guardando ? <CircularProgress size={16} sx={{ color: "#6c757d" }} /> : <SaveIcon />}
                            sx={BOTON_SECUNDARIO_SX}
                        >
                            {guardando ? "Guardando..." : `Guardar${pendientes > 0 ? ` (${pendientes})` : ""}`}
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!puedeFinalizar || guardando || bloqueado}
                            onClick={handleFinalizar}
                            startIcon={finalizando ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DoneAllIcon />}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            {finalizando ? "Finalizando..." : "Finalizar inventario"}
                        </Button>
                    </Stack>
                )}
            </Card>

            <ModalQuitarNave
                show={naveAQuitar !== null}
                onHide={() => setNaveAQuitar(null)}
                onConfirm={confirmarQuitarNave}
                conteo={naveAQuitar}
                loading={quitandoNave !== null}
            />
        </Box>
    );
};

export default ConteoInventario;
